import { Link } from 'react-router-dom'
import { assets } from '../../config/assets'
import { openAnalyticsConsent } from '../../lib/marketing'

export function Footer() {
  const year = new Date().getFullYear()

  return (
    <footer className="bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* Column 1 -Brand */}
          <div className="col-span-2 lg:col-span-1 text-center lg:text-left">
            <Link to="/" className="inline-block mb-4">
              <img
                src={assets.logos.primaryNav}
                alt="Advantage Horsemanship"
                width={454}
                height={72}
                loading="lazy"
                style={{ height: '32px', width: 'auto', display: 'block' }}
              />
            </Link>
            <p className="font-body text-sm text-white/70 leading-relaxed mb-6">
              Helping riders fix the real problem. The communication, the confidence, and the partnership.
            </p>
            <SocialLinks />

            {/* A2P 10DLC: carriers require the business name and full contact
                details (address, phone, email) to be publicly displayed. */}
            <address className="not-italic mt-6 space-y-1 font-body text-sm text-white/60 leading-relaxed">
              <div className="text-white/80 font-semibold">Advantage Horsemanship LLC</div>
              <div>129 Hartwood Rd</div>
              <div>Fredericksburg, VA 22406</div>
              <div>
                <a
                  href="tel:+18775746773"
                  className="hover:text-gold transition-colors duration-150"
                >
                  (877) 574-6773
                </a>
              </div>
              <div>
                <a
                  href="mailto:info@advantagehorsemanship.com"
                  className="hover:text-gold transition-colors duration-150"
                >
                  info@advantagehorsemanship.com
                </a>
              </div>
            </address>
          </div>

          {/* Column 2 -Programs */}
          <div>
            <h3 className="font-body text-xs font-semibold tracking-widest uppercase text-white/50 mb-4">
              Programs
            </h3>
            <ul className="space-y-3">
              {([
                { to: 'https://riderswayapp.com', label: "Rider's Way App", external: true },
                { to: '/accelerator', label: 'Accelerator Class' },
                { to: '/horse-sense', label: 'Horse Sense' },
                { to: '/private-sessions', label: 'Private Sessions' },
                { to: '/masterclass', label: 'Masterclasses' },
                { to: '/schedule', label: 'Schedule' },
                { to: '/programs', label: 'All Programs' },
              ] as { to: string; label: string; external?: boolean }[]).map(({ to, label, external }) => (
                <li key={to}>
                  {external ? (
                    <a
                      href={to}
                      className="font-body text-sm text-white/80 hover:text-gold transition-colors duration-150 min-h-[52px] inline-flex items-center"
                    >
                      {label}
                    </a>
                  ) : (
                    <Link
                      to={to}
                      className="font-body text-sm text-white/80 hover:text-gold transition-colors duration-150 min-h-[52px] inline-flex items-center"
                    >
                      {label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 -Company */}
          <div>
            <h3 className="font-body text-xs font-semibold tracking-widest uppercase text-white/50 mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {[
                { to: '/about', label: 'About Scott' },
                { to: '/store', label: 'Store' },
                { to: '/contact', label: 'Contact' },
                { to: '/support', label: "Rider's Way Support" },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="font-body text-sm text-white/80 hover:text-gold transition-colors duration-150 min-h-[52px] inline-flex items-center"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 -Legal */}
          <div className="col-span-2 lg:col-span-1">
            <h3 className="font-body text-xs font-semibold tracking-widest uppercase text-white/50 mb-4">
              Legal
            </h3>
            <ul className="space-y-3">
              {[
                { to: '/privacy-policy', label: 'Privacy Policy' },
                { to: '/terms', label: 'Terms of Service' },
                { to: '/refund-policy', label: 'Refund Policy' },
                { to: '/text-updates', label: 'Text Updates' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="font-body text-sm text-white/80 hover:text-gold transition-colors duration-150 min-h-[52px] inline-flex items-center"
                  >
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={openAnalyticsConsent}
                  className="font-body text-sm text-white/80 hover:text-gold transition-colors duration-150 min-h-[52px] inline-flex items-center"
                >
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-white/40">
            &copy; {year} Advantage Horsemanship. All rights reserved.
          </p>
          <p className="font-body text-xs text-white/30">
            Scott Purdum, Horse Behaviorist &amp; Rider Confidence Coach
          </p>
        </div>
      </div>
    </footer>
  )
}

function SocialLinks() {
  const socials = [
    {
      label: 'YouTube',
      href: 'https://www.youtube.com/scottpurdum',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-5 h-5">
          <path d="M23.498 6.186a2.998 2.998 0 00-2.11-2.12C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.388.52a2.998 2.998 0 00-2.11 2.121C0 8.07 0 12 0 12s0 3.93.502 5.814a2.998 2.998 0 002.11 2.12c1.883.52 9.388.52 9.388.52s7.505 0 9.388-.52a2.998 2.998 0 002.11-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
    },
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/advantagehorsemanship',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-5 h-5">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.247 2.242 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.247-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.247-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608C4.516 2.567 5.783 2.295 7.15 2.233 8.416 2.175 8.796 2.163 12 2.163zm0 1.838c-3.148 0-3.5.011-4.737.068-1.004.046-1.548.213-1.91.354a3.2 3.2 0 00-1.164.757 3.2 3.2 0 00-.757 1.164c-.14.362-.308.906-.354 1.91C3.02 8.5 3.01 8.853 3.01 12s.011 3.5.068 4.737c.046 1.004.213 1.548.354 1.91.164.42.395.795.757 1.164.369.362.744.593 1.164.757.362.14.906.308 1.91.354 1.236.057 1.588.068 4.737.068 3.148 0 3.5-.011 4.737-.068 1.004-.046 1.548-.213 1.91-.354a3.2 3.2 0 001.164-.757c.362-.369.593-.744.757-1.164.14-.362.308-.906.354-1.91.057-1.236.068-1.588.068-4.737 0-3.148-.011-3.5-.068-4.737-.046-1.004-.213-1.548-.354-1.91a3.2 3.2 0 00-.757-1.164 3.2 3.2 0 00-1.164-.757c-.362-.14-.906-.308-1.91-.354C15.5 3.01 15.148 3 12 3zm0 3.135a5.865 5.865 0 110 11.73 5.865 5.865 0 010-11.73zm0 1.838a4.027 4.027 0 100 8.054 4.027 4.027 0 000-8.054zm6.084-2.01a1.37 1.37 0 11-2.74 0 1.37 1.37 0 012.74 0z" />
        </svg>
      ),
    },
    {
      label: 'Facebook',
      href: 'https://www.facebook.com/advantagehorsemanship/',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-5 h-5">
          <path d="M22.675 0H1.325C.593 0 0 .593 0 1.326v21.348C0 23.407.593 24 1.325 24h11.495v-9.294H9.691V11.24h3.129V8.59c0-3.1 1.894-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.714-1.795 1.762v2.31h3.587l-.467 3.466h-3.12V24h6.116C23.406 24 24 23.407 24 22.674V1.326C24 .593 23.406 0 22.675 0z" />
        </svg>
      ),
    },
    {
      label: 'TikTok',
      href: 'https://www.tiktok.com/@scottpurdum',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-5 h-5">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.19 8.19 0 004.79 1.53V6.75a4.85 4.85 0 01-1.02-.06z" />
        </svg>
      ),
    },
  ]

  return (
    <div className="flex items-center justify-center lg:justify-start gap-4">
      {socials.map(({ label, href, icon }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="text-white/60 hover:text-gold transition-colors duration-150 inline-flex items-center justify-center w-10 h-10"
        >
          {icon}
        </a>
      ))}
    </div>
  )
}
